import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Typography } from '@mui/material';
import { CheckboxCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { submitOtpSignInAction } from 'store/auth/actions';

import { BACK_TO_PASSWORD_LABEL, USE_PASSWORD_TO_SIGN_IN_MESSAGE } from '../../Login.constants';
import { LoginTestIds } from '../../Login.testIds';
import { isUsePasswordToSignInError } from '../../Login.utils';

import { OTP_CODE_LENGTH } from './OtpLoginTab.constants';

const InputLabelProps = { shrink: true };
const inputProps = { maxLength: OTP_CODE_LENGTH, inputMode: 'numeric' };

/**
 * @param {Function|undefined} onActivationRequired - DEV-4157: called with { activationToken }
 *   when the OTP response carries a constrained activation token instead of a full session.
 */
const OtpLoginTab = ({
  signInRequestId,
  showRememberDevice,
  onActivationRequired,
  onBack,
  onPasswordSignInRequired,
}) => {
  const dispatch = useDispatch();
  const login = useSelector(({ auth }) => auth.login);

  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onCodeChange = useCallback(e => {
    setCode(e.target.value.replace(/\D/g, ''));
    setError(null);
  }, []);

  const onRememberDeviceChange = useCallback(e => {
    setRememberDevice(e.target.checked);
  }, []);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!code || code.length !== OTP_CODE_LENGTH) {
        setError(`Please enter your ${OTP_CODE_LENGTH}-digit code`);

        return;
      }

      setIsSubmitting(true);

      try {
        const result = await dispatch(
          submitOtpSignInAction({ requestId: signInRequestId, code, rememberDevice }),
        );

        // DEV-4157: activation-only token — must set a password before completing sign-in.
        if (result?.requiresPasswordSetup && onActivationRequired) {
          onActivationRequired({ activationToken: result.activationToken });
        }
      } catch (submitError) {
        if (onPasswordSignInRequired && isUsePasswordToSignInError(submitError)) {
          onPasswordSignInRequired(USE_PASSWORD_TO_SIGN_IN_MESSAGE);

          return;
        }

        setError('Invalid or expired code. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      code,
      dispatch,
      onActivationRequired,
      onPasswordSignInRequired,
      rememberDevice,
      signInRequestId,
    ],
  );

  const isDisabled = isSubmitting || login?.loading;

  return (
    <div className="w-full">
      <Typography className="mb-16 text-center" color="text.secondary" variant="medium14">
        Enter the verification code sent to your email or phone.
      </Typography>

      <form noValidate className="flex flex-col justify-center w-full" onSubmit={onSubmit}>
        <TextFieldCDS
          required
          withStaticHelper
          InputLabelProps={InputLabelProps}
          className="mb-16"
          dataTestId={LoginTestIds.OTP_CODE_INPUT}
          disabled={isDisabled}
          error={!!error}
          helperText={error}
          inputProps={inputProps}
          label="Verification Code"
          name="code"
          type="text"
          value={code}
          variant="outlined"
          onChange={onCodeChange}
        />

        <CheckboxCDS
          dataTestId={LoginTestIds.OTP_REMEMBER_DEVICE_CHECKBOX}
          isHidden={!showRememberDevice}
          label="Remember this device"
          value={rememberDevice}
          onChange={onRememberDeviceChange}
        />

        <Button
          aria-label="VERIFY"
          className="w-full mx-auto mt-16 normal-case"
          color="primary"
          data-testid={LoginTestIds.OTP_VERIFY_BUTTON}
          disabled={isDisabled || !code}
          type="submit"
          variant="contained"
        >
          Verify
        </Button>

        {onBack && (
          <Button
            className="w-full mx-auto mt-8 normal-case"
            color="primary"
            data-testid={LoginTestIds.OTP_BACK_BUTTON}
            type="button"
            variant="text"
            onClick={onBack}
          >
            {BACK_TO_PASSWORD_LABEL}
          </Button>
        )}
      </form>
    </div>
  );
};

OtpLoginTab.propTypes = {
  signInRequestId: PropTypes.string.isRequired,
  showRememberDevice: PropTypes.bool,
  onActivationRequired: PropTypes.func,
  onBack: PropTypes.func,
  onPasswordSignInRequired: PropTypes.func,
};

export default OtpLoginTab;
